import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatures } from '../../../utils/featureContext';
import { FEATURES } from '../../../utils/extractFeaturesList';
import { getDirectories, getFiles, getPresignedUrl, deleteFile } from '../../../services/scm/FileManagement';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCirclePlus, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import TreeView from '../../../components/Tree Structure/TreeView';
import { buildTreeData } from '../../../utils/buildTreeData';
import { createPackage, updatePackage, getPackageInfo } from '../../../services/scm/Package';
import { getAssetTypes, getJobType, getDestinationByKey } from '../../../services/scm/Utils';
import Grid from '../../../components/grid/Grid';
import UploadFileDialog from '../../../components/uploadFileDialog/UploadFileDialog';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { formatToBrowserTime, formatToUtcIso } from '../../../utils/timeFormat';
import CustomInput from '../../../components/textField/scmInput/CustomInput';
import './CreatePackage.css';
import { toast } from 'react-toastify';
import axios from 'axios';
import LoadingOverlay from '../../../components/loadingoverlay/LoadingOverlay';

function findNodeByPath(nodes = [], targetPath = '') {
    if (!targetPath) return null;
    for (const n of nodes) {
        if (n.path === targetPath) return n;
        if (Array.isArray(n.children) && n.children.length) {
            const found = findNodeByPath(n.children, targetPath);
            if (found) return found;
        }
    }
    return null;
}

export default function CreateOrEditPackage({ title = 'Create Package' }) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // ---- EDIT MODE DETECTION (FROM STATE) ----
    const statePkgName = location.state?.packageName || null;
    const stateVersionName = location.state?.versionName || null; // e.g. "v1.0.0"
    const isEdit = !!(statePkgName && stateVersionName);

    const [editPkg, setEditPkg] = useState(null);
    const [pkgLoading, setPkgLoading] = useState(isEdit);
    const [saveLoading, setSaveLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const pageTitle = isEdit ? t('Edit Package') : t(title);

    // ---- FORM STATE ----
    const [packageName, setPackageName] = useState('');
    const [description, setDescription] = useState('');
    const [version, setVersion] = useState('1.0.0');
    const [expirationMode, setExpirationMode] = useState('Never'); // 'Never' | 'Date'
    const [expirationDate, setExpirationDate] = useState(dayjs());
    const [assetType, setAssetType] = useState('');
    const [assetTypes, setAssetTypes] = useState([]);
    const [jobType, setJobType] = useState('');
    const [clearTarget, setClearTarget] = useState(false);

    // tree + files
    const [treeItems, setTreeItems] = useState([]);
    const [selectedNodePath, setSelectedNodePath] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [treeLoading, setTreeLoading] = useState(false);
    const [fileLoading, setFileLoading] = useState(false);
    const [data, setData] = useState([]);

    const [open, setOpen] = useState(false);

    const [allPackages, setAllPackages] = useState([]);
    const [checkingCombo, setCheckingCombo] = useState(false);
    const [isComboUnique, setIsComboUnique] = useState(true);

    const { features, loading: featuresLoading } = useFeatures();
    const isScmAdmin = !featuresLoading && features?.includes(FEATURES.SCM_ADMIN);

    useEffect(() => {
        async function loadAllPackages() {
            try {
                const raw = await getPackageInfo({
                    status: "both",
                    expired: "both"
                });

                setAllPackages(Array.isArray(raw) ? raw : []);
            } catch (err) {
                console.error("Failed to load packages", err);
            }
        }

        loadAllPackages();
    }, []);

    useEffect(() => {
        if (isEdit) {
            setIsComboUnique(true);
            setCheckingCombo(false);
            return;
        }

        const name = packageName.trim();

        if (!name) {
            setIsComboUnique(true);
            setCheckingCombo(false);
            return;
        }

        setCheckingCombo(true);

        const timeout = setTimeout(() => {
            const normalizedName = name.toLowerCase();

            const exists = allPackages.some(p =>
                p.packageName?.toLowerCase() === normalizedName
            );

            setIsComboUnique(!exists);
            setCheckingCombo(false);
        }, 300);

        return () => clearTimeout(timeout);

    }, [packageName, allPackages, isEdit]);




    // ---- DIRTY-CHECK SNAPSHOT ----
    const initialRef = useRef({
        packageName: '',
        description: '',
        version: '1.0.0',
        assetType: '',
        jobType: '',
        clearTarget: false,
        expirationMode: 'Never', // 'Never' | 'Date'
        initialS3Keys: [],
    });
    const initialExpiryRef = useRef(dayjs());

    // ---- HELPERS ----
    const Required = () => <span className="text-danger ms-1">*</span>;
    const isNonEmpty = (v) => String(v ?? '').trim().length > 0;

    const fetchPackage = async () => {
        if (!isEdit) return;
        try {
            setPkgLoading(true);

            // getPackageInfo returns flat array
            const flatList = await getPackageInfo({ packageName: statePkgName });

            const found = flatList.find(
                (p) =>
                    p.packageName === statePkgName &&
                    p.version === stateVersionName // version field from helper (e.g. "v1.0.0")
            );

            if (!found) {
                toast.error(t('Package/version not found'));
                navigate(-1);
                return;
            }

            setEditPkg(found);
        } catch (err) {
            console.error('Failed to load package info', err);
            toast.error(t('Failed to load package info'));
            navigate(-1);
        } finally {
            setPkgLoading(false);
        }
    };

    // ---- APPLY EDIT PACKAGE TO FORM STATE ----
    const applyEditPkgToForm = (pkg) => {
        if (!pkg) return;

        const expiryIsNever = pkg.expiration === 'Never';
        const expiryDayjs = expiryIsNever ? dayjs() : dayjs(pkg.expiration);

        setPackageName(pkg.packageName || '');
        setDescription(pkg.description || '');
        setVersion(pkg.version ? pkg.version.replace(/^v/i, '') : '1.0.0');
        setExpirationMode(expiryIsNever ? 'Never' : 'Date');
        setExpirationDate(expiryDayjs);
        setAssetType(pkg.assetType || '');
        setJobType(pkg.jobType || '');
        setClearTarget(!!pkg.clearTarget);

        setSelectedRows(
            Array.isArray(pkg.s3Keys)
                ? pkg.s3Keys.map((p, i) => ({ id: i + 1, path: p, name: p }))
                : []
        );

        initialRef.current = {
            packageName: pkg.packageName || '',
            description: pkg.description || '',
            version: pkg.version ? pkg.version.replace(/^v/i, '') : '1.0.0',
            assetType: pkg.assetType || '',
            jobType: pkg.jobType || '',
            clearTarget: !!pkg.clearTarget,
            expirationMode: expiryIsNever ? 'Never' : 'Date',
            initialS3Keys: Array.isArray(pkg.s3Keys) ? [...pkg.s3Keys] : [],
        };
        initialExpiryRef.current = expiryDayjs;
    };


    // ---- TABLE HEADERS ----
    const headers = useMemo(
        () => [
            { key: 'name', label: 'File Name', width: '50%' },
            { key: 'size', label: 'Size(Bytes)', width: '20%' },
            { key: 'date', label: 'Date', width: '30%' },
        ],
        []
    );

    // ---- TREE HANDLERS ----
    const handleTreeClick = async (node) => {
        if (!node?.path) return;
        setSelectedNodePath(node.path);
        try {
            setFileLoading(true);
            const table_data = await getFiles({ directory: node.path });
            const files = table_data?.files ?? [];
            const mapped = files.map((file, index) => ({
                id: index + 1,
                name: file.filename,
                size: file.size,
                date: file?.last_modified ? formatToBrowserTime(file.last_modified) : '',
                path: file.key,
            }));
            setData(mapped);
        } catch (err) {
            console.error('Error loading directory contents:', err);
        } finally {
            setFileLoading(false);
        }
    };

    const loadDirectories = async (selectPath) => {
        try {
            setTreeLoading(true);
            const raw = await getDirectories();
            const treeViewFormatted = buildTreeData(raw?.items);
            setTreeItems(treeViewFormatted);

            if (selectPath) {
                const node = findNodeByPath(treeViewFormatted, selectPath);
                if (node) {
                    setSelectedNodePath(selectPath);
                    await handleTreeClick(node);
                }
            }
        } catch (err) {
            console.error('loadDirectories error:', err);
        } finally {
            setTreeLoading(false);
        }
    };

    const loadAssetTypes = async () => {
        const raw = await getAssetTypes();
        setAssetTypes(raw?.thingTypes || []);
    };

    // ---- STATIC + DERIVED DATA ----
    const jobTypes = useMemo(() => getJobType(), []);
    const derivedDestination = useMemo(
        () => getDestinationByKey(jobType) || editPkg?.destination || '',
        [jobType, editPkg?.destination]
    );

    useEffect(() => {
        loadDirectories();
        loadAssetTypes();

        if (isEdit) {
            fetchPackage();
        }
    }, [isEdit, statePkgName, stateVersionName]);

    useEffect(() => {
        if (isEdit && editPkg) {
            applyEditPkgToForm(editPkg);
        }
    }, [isEdit, editPkg]);

    const selectedFilePaths = useMemo(() => {
        if (!Array.isArray(selectedRows) || selectedRows.length === 0) return '';
        return selectedRows
            .map((row) => `"${row?.path || ''}"`)
            .filter(Boolean)
            .join('; ');
    }, [selectedRows]);

    // ---- VALIDATION ----
    const hasSelectedFiles = Array.isArray(selectedRows) && selectedRows.length > 0;
    const hasInitialFiles = initialRef.current.initialS3Keys?.length > 0;
    const hasFiles = hasSelectedFiles || (isEdit && hasInitialFiles);

    const isFormValid = useMemo(() => {
        return (
            isNonEmpty(packageName) &&
            isNonEmpty(version) &&
            isNonEmpty(assetType) &&
            isNonEmpty(derivedDestination) &&
            hasFiles &&
            isComboUnique &&
            !checkingCombo
        );
    }, [packageName, version, assetType, derivedDestination, hasFiles, isComboUnique, checkingCombo]);
    // ---- DIRTY CHECK ----
    const isDirty = useMemo(() => {
        const init = initialRef.current;
        if (packageName !== init.packageName) return true;
        if (description !== init.description) return true;
        if (version !== init.version) return true;
        if (assetType !== init.assetType) return true;
        if (jobType !== init.jobType) return true;
        if (clearTarget !== init.clearTarget) return true;

        const currentPaths = (selectedRows || []).map((r) => r.path).sort((a, b) => a.localeCompare(b));
        const initPaths = (init.initialS3Keys || []).slice().sort((a, b) => a.localeCompare(b));
        const filesChanged =
            currentPaths.length !== initPaths.length ||
            currentPaths.some((p, i) => p !== initPaths[i]);
        if (filesChanged) return true;

        if (expirationMode !== init.expirationMode) {
            if (expirationMode !== 'Never') return true;
        }
        if (expirationMode === 'Date') {
            const initialDate = initialExpiryRef.current;
            if (!dayjs(expirationDate).isSame(initialDate)) return true;
        }
        return false;
    }, [
        packageName,
        description,
        version,
        assetType,
        jobType,
        clearTarget,
        selectedRows,
        expirationMode,
        expirationDate,
    ]);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (!isDirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // ---- RESET FORM ----
    const resetForm = () => {
        const now = dayjs();

        if (isEdit && editPkg) {
            applyEditPkgToForm(editPkg);
        } else {
            setPackageName('');
            setDescription('');
            setVersion('1.0.0');
            setExpirationMode('Never');
            setExpirationDate(now);
            setAssetType('');
            setJobType('');
            setClearTarget(false);
            setSelectedRows([]);
            setSelectedNodePath('');
            setData([]);

            initialRef.current = {
                packageName: '',
                description: '',
                version: '1.0.0',
                assetType: '',
                jobType: '',
                clearTarget: false,
                expirationMode: 'Never',
                initialS3Keys: [],
            };
            initialExpiryRef.current = now;
        }
    };

    // ---- SAVE ----
    const handleSave = async () => {
        if (!isFormValid) return;

        const finalPackageName = isEdit ? statePkgName : packageName;

        const payload = {
            packageName: finalPackageName,
            description,
            versionName: 'v' + version,
            expiryDate: expirationMode === 'Date' ? formatToUtcIso(expirationDate) : 'Never',
            assetType,
            jobType,
            clearTarget,
            destination: derivedDestination,
            s3Keys: hasSelectedFiles
                ? selectedRows.map((r) => r.path)
                : initialRef.current.initialS3Keys,
        };

        try {
            setSaveLoading(true);
            if (isEdit) {
                await updatePackage(payload);
                toast.success(`${finalPackageName}:v${version} ${t('Updated')}`);
                navigate(-1);
            } else {
                await createPackage(payload);
                toast.success(`${finalPackageName}:v${version} ${t('Saved')}`);
                resetForm();
                await loadDirectories();

                navigate('/software-management/select-dependencies', {
                    state: {
                        packageName: finalPackageName,
                        versionName: 'v' + version,
                    },
                });
            }
        } catch (err) {
            console.error('save error', err);
            toast.error(t('Something went wrong, Please try again.'));
        } finally {
            setSaveLoading(false);
        }

    };

    // ---- UPLOAD ----
    const handleUpload = async ({ path, file }) => {
        const toastId = toast.loading('Uploading file... 0%', { progress: 0 });
        const contentType = file.type || 'application/octet-stream';

        try {
            const res = await getPresignedUrl({ key: path, contentType });
            const presignedUrl = res.uploadUrl;

            const bareAxios = axios.create();
            await bareAxios.put(presignedUrl, file, {
                headers: { 'Content-Type': contentType },
                onUploadProgress: (event) => {
                    if (!event.total) return;

                    const progress = Math.round((event.loaded / event.total) * 100);
                    toast.update(toastId, {
                        render: `Uploading... ${progress}%`,
                        progress: progress / 100,
                    });
                },
            });

            toast.update(toastId, {
                render: t('File uploaded successfully'),
                type: 'success',
                isLoading: false,
                autoClose: 3000,
                progress: undefined,
            });

            const folderPath = path.includes('/')
                ? path.substring(0, path.lastIndexOf('/') + 1)
                : '';

            setOpen(false);
            await loadDirectories(folderPath);

        } catch (err) {
            console.error('upload error', err);

            toast.update(toastId, {
                render: t('Upload failed'),
                type: 'error',
                isLoading: false,
                autoClose: 4000,
                progress: undefined,
            });
        }
    };


    const selectedFile = useMemo(
        () => (Array.isArray(selectedRows) && selectedRows.length === 1 ? selectedRows[0] : null),
        [selectedRows]
    );

    const handleDeleteSelectedFile = async () => {
        if (!selectedFile) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete "${selectedFile.name}"?`
        );
        if (!confirmed) return;

        setDeleteLoading(true);
        try {
            await deleteFile({ key: selectedFile.path });
            toast.success(`File "${selectedFile.name}" deleted successfully`);

            // Refresh the current folder contents after delete
            if (selectedNodePath) {
                await handleTreeClick({ path: selectedNodePath });
            } else {
                setData([]);
            }

            setSelectedRows([]);
        } catch (err) {
            const is409 = err?.response?.status === 409;
            const apiError = err?.response?.data?.error;

            const errorMessage = is409 && apiError
                ? `Cannot delete file "${selectedFile.name}". ${apiError}`
                : `Failed to delete file "${selectedFile.name}". Please try again.`;

            toast.error(errorMessage);
            console.error("Delete File Failed", err);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="container my-3 ">
            <div className="card-body col-12 col-xxl-10 responsive-col">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">{pageTitle}</h5>
                </div>

                <div className="row g-2">
                    <div className="col-12 col-md-6">
                        <CustomInput
                            label={
                                <>
                                    {t('Package name')}
                                    <Required />
                                </>
                            }
                            value={packageName}
                            onChange={(e) => setPackageName(e.target.value)}
                            placeholder={t('Enter package name')}
                            allowedChars={/^[A-Za-z0-9_-]*$/}
                            disabled={isEdit} // lock in edit mode
                        />
                        {!isNonEmpty(packageName) && (
                            <small className="text-danger">
                                {t('Package name is required')}
                            </small>
                        )}

                        {isNonEmpty(packageName) && !isComboUnique && !checkingCombo && (
                            <small className="text-danger">
                                {t('Package name already exists')}
                            </small>
                        )}

                        {isNonEmpty(packageName) && checkingCombo && (
                            <small className="text-muted">
                                {t('Checking availability...')}
                            </small>
                        )}

                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label">
                            {t('Version')} <Required />
                        </label>
                        <div className="input-group">
                            <span className="input-group-text">v</span>
                            <CustomInput
                                inGroup
                                value={version}
                                onChange={(e) => setVersion(e.target.value)}
                                allowedChars={/^[0-9.]*$/}
                                placeholder="1.0.0"
                                disabled={isEdit} // lock in edit mode
                            />
                        </div>
                        {!isNonEmpty(version) && (
                            <small className="text-danger">{t('Version is required')}</small>
                        )}
                    </div>

                    <div className="col-12 col-md-6">
                        <CustomInput
                            label={t('Description')}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('Enter Description')}
                        />
                    </div>

                    <div className="col-12 col-md-6">
                        <label className="form-label d-block">{t('Expiration')}</label>
                        <div className="d-flex align-items-center gap-2">
                            <div className="btn-group" role="group" aria-label="expiration buttons">
                                <button
                                    type="button"
                                    className={`btn ${expirationMode === 'Never'
                                        ? 'btn-primary'
                                        : 'btn-outline-secondary'
                                        }`}
                                    onClick={() => setExpirationMode('Never')}
                                >
                                    {t('Never')}
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${expirationMode === 'Date'
                                        ? 'btn-primary'
                                        : 'btn-outline-secondary'
                                        }`}
                                    onClick={() => setExpirationMode('Date')}
                                >
                                    {t('Date')}
                                </button>
                            </div>

                            {expirationMode === 'Date' && (
                                <div style={{ width: '100%' }}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DateTimePicker
                                            value={expirationDate}
                                            onChange={(newValue) => setExpirationDate(newValue)}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: 'outlined',
                                                    size: 'small',
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="col-12 col-md-4">
                        <label className="form-label">
                            {t('Asset Type')} <Required />
                        </label>
                        <select
                            value={assetType}
                            onChange={(e) => setAssetType(e.target.value)}
                            className="form-select"
                        >
                            <option value="">{t('Select Asset Type')}</option>
                            {assetTypes.map((key) => (
                                <option key={key} value={key}>
                                    {key}
                                </option>
                            ))}
                        </select>
                        {!isNonEmpty(assetType) && (
                            <small className="text-danger">
                                {t('Asset Type is required')}
                            </small>
                        )}
                    </div>
                </div>

                <div className="card mt-3">
                    <div className="card-body">
                        <h6 className="card-subtitle mb-3">{t('Package Instructions')}</h6>

                        <div className="row g-2">
                            <div className="col-12 col-md-4">
                                <label className="form-label">{t('Job Type')}</label>
                                <select
                                    value={jobType}
                                    onChange={(e) => setJobType(e.target.value)}
                                    className="form-select"
                                >
                                    <option value="">{t('Select Job Type')}</option>
                                    {jobTypes.map((key) => (
                                        <option key={key} value={key}>
                                            {key}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-12 col-md-4">
                                <label className="form-label d-block">{t('Clear target')}</label>
                                <div className="btn-group" role="group" aria-label="clear target">
                                    <button
                                        type="button"
                                        className={`btn ${clearTarget ? 'btn-primary' : 'btn-outline-secondary'
                                            }`}
                                        onClick={() => setClearTarget(true)}
                                    >
                                        {t('True')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn ${!clearTarget ? 'btn-primary' : 'btn-outline-secondary'
                                            }`}
                                        onClick={() => setClearTarget(false)}
                                    >
                                        {t('False')}
                                    </button>
                                </div>
                            </div>

                            <div className="col-12">
                                <label className="form-label">
                                    {t('Destination')} <Required />
                                </label>
                                <input
                                    readOnly
                                    value={derivedDestination}
                                    className="form-control-plaintext readonly-input"
                                />
                                {!isNonEmpty(derivedDestination) && (
                                    <small className="text-danger">
                                        {t('Destination is required (choose a Job Type)')}
                                    </small>
                                )}
                            </div>
                        </div>

                        <hr className="my-3" />

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <label className="form-label mb-0">{t('File Browser')}</label>

                                {selectedNodePath && (
                                    <span
                                        className="badge bg-light text-dark border cp-path-badge text-truncate"
                                        title={selectedNodePath || t('No folder selected')}
                                    >
                                        {selectedNodePath}
                                    </span>
                                )}
                            </div>

                            <div className="ms-auto d-flex mx-2">
                                <button
                                    className="btn btn-download"
                                    onClick={() => setOpen(true)}
                                    title={t('Upload File')}
                                >
                                    <FontAwesomeIcon icon={faFileCirclePlus} />
                                </button>

                                {isScmAdmin && (
                                    <button
                                        className="btn btn-download"
                                        type="button"
                                        disabled={!selectedFile || deleteLoading}
                                        onClick={handleDeleteSelectedFile}
                                        title={
                                            selectedFile
                                                ? t('Delete selected file')
                                                : t('Select a single file to delete')
                                        }
                                    >
                                        {deleteLoading ? (
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                        ) : (
                                            <FontAwesomeIcon icon={faTrash} />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="row g-2 col-12 col-md-12">
                            <div className="col-12 col-md-4 treeview-container">
                                <TreeView
                                    data={treeItems}
                                    onNodeClick={handleTreeClick}
                                    selectedNodePath={selectedNodePath}
                                    loading={treeLoading}
                                />
                            </div>

                            <div className="col-12 col-md-8 grid-container">
                                <Grid
                                    loading={fileLoading}
                                    headers={headers}
                                    data={data}
                                    rowsPerPage={1000}
                                    canMultiSelect={true}
                                    onSelectionChange={setSelectedRows}
                                    showPagination={false}
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label">
                                    {t('Selected Files')} <Required />
                                </label>
                                <input
                                    readOnly
                                    value={
                                        selectedFilePaths ||
                                        (isEdit
                                            ? initialRef.current.initialS3Keys.join('; ')
                                            : '')
                                    }
                                    className="form-control-plaintext readonly-input"
                                    title={
                                        selectedFilePaths ||
                                        (isEdit
                                            ? initialRef.current.initialS3Keys.join('; ')
                                            : '')
                                    }
                                />
                                {!hasFiles && (
                                    <small className="text-danger">
                                        {t('Select at least one file')}
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-4 d-flex justify-content-end gap-2 col-12 col-xxl-10 responsive-col">
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                        if (isDirty) {
                            const confirmed = window.confirm(
                                t('Unsaved changes will be lost. Continue?')
                            );
                            if (!confirmed) return;
                        }
                        if (isEdit) {
                            navigate(-1);
                        } else {
                            window.location.reload();
                        }
                    }}
                >
                    {t('Cancel')}
                </button>

                <button
                    className="btn btn-primary"
                    disabled={!isFormValid}
                    onClick={handleSave}
                >
                    {t(isEdit ? 'Update' : 'Save')}
                </button>
            </div>

            <UploadFileDialog
                open={open}
                onClose={() => setOpen(false)}
                initialPath={selectedNodePath}
                onUpload={handleUpload}
            />
            {isEdit && pkgLoading && <LoadingOverlay />}
            {saveLoading && <LoadingOverlay />}
            {deleteLoading && <LoadingOverlay />}
        </div>
    );
}
